import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import Div from "@jumbo/shared/Div";
import LoadingButton from "@mui/lab/LoadingButton";
import { Card, CardContent, Checkbox, Dialog, FormControlLabel, Typography } from "@mui/material";
import CardMedia from "@mui/material/CardMedia";
import Link from "@mui/material/Link";
import { alpha } from "@mui/material/styles";
import { postLoginUserApi } from 'api/auth';
import { hasKurulRole, swal_error, swal_role_denied, swal_success } from "app/utils/appHelpers";
import { ASSET_IMAGES } from 'app/utils/constants/paths';
import config from 'config';
import { Form, Formik } from "formik";
import { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import * as yup from 'yup';


const validationSchema = yup.object({
    email: yup
        .string('E-Posta veya Kimlik Numaranızı Giriniz')
        .required('E-Posta veya Kimlik Numarası zorunludur'),
    password: yup
        .string('Şifrenizi giriniz')
        .required('Şifre girişi zorunludur'),
});

function LoginDialog({ open, onClose, afterLogin }) {
    const recaptchaRef = useRef(null);
    const pendingCredentialsRef = useRef(null);
    const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const siteKey = config.googleCaptchaSiteKey;

    const handleClose = (event, reason) => {
        if (reason && reason === "backdropClick" && "escapeKeyDown")
            return;
        onClose();
    }

    const loginUser = async (email, password, recaptchaToken) => {
        setIsLoggingIn(true);
        try {
            const res = await postLoginUserApi({ email, password, recaptchaToken });
            if (res.status) {
                if (!hasKurulRole(res.user?.userRoles)) {
                    await swal_role_denied();
                    return;
                }
                swal_success("Hoşgeldin " + res.user.name + ", Kullanıcı girişi başarılı");
                afterLogin(res.user, res.token);
                onClose();
            } else {
                swal_error(res.message || 'Giriş yapılamadı. Lütfen tekrar deneyin.');
            }
        } catch (error) {
            swal_error(error?.response?.data?.message || 'Giriş yapılamadı. Lütfen tekrar deneyin.');
        } finally {
            setIsLoggingIn(false);
            pendingCredentialsRef.current = null;
        }
    }

    const handleRecaptchaChange = (recaptchaToken) => {
        const pending = pendingCredentialsRef.current;
        if (!recaptchaToken || !pending) {
            return;
        }
        loginUser(pending.email, pending.password, recaptchaToken);
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
    }

    const handleRecaptchaError = () => {
        pendingCredentialsRef.current = null;
        swal_error('Güvenlik doğrulaması yüklenemedi. Lütfen sayfayı yenileyin.');
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
    }

    const handleRecaptchaExpired = () => {
        pendingCredentialsRef.current = null;
        swal_error('Güvenlik doğrulamasının süresi doldu. Lütfen tekrar deneyin.');
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
    }

    return (
        <Dialog open={open} onClose={handleClose} disableEscapeKeyDown >

            <Div sx={{
                flex: 1,
                flexWrap: 'wrap',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: theme => theme.spacing(4),
            }}>

                <Card sx={{ maxWidth: '100%', width: 360, mb: 4 }}>
                    <Div sx={{ position: 'relative', height: '200px' }}>
                        <CardMedia
                            component="img"
                            alt="green iguana"
                            image={`${ASSET_IMAGES}/logos/kurulsis.png`}
                        />
                        <Div
                            sx={{
                                flex: 1,
                                inset: 0,
                                position: 'absolute',
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: theme => alpha(theme.palette.common.black, .5),
                                p: theme => theme.spacing(3),
                            }}
                        />
                    </Div>
                    <CardContent sx={{ pt: 3 }}>

                        <Formik
                            validateOnChange={true}
                            initialValues={{
                                email: '',
                                password: '',
                            }}
                            validationSchema={validationSchema}
                            onSubmit={(data, { setSubmitting }) => {
                                if (!siteKey || !recaptchaLoaded || !recaptchaRef.current) {
                                    swal_error('Güvenlik doğrulaması henüz hazır değil. Lütfen bekleyin.');
                                    setSubmitting(false);
                                    return;
                                }

                                pendingCredentialsRef.current = {
                                    email: data.email,
                                    password: data.password,
                                };
                                setSubmitting(false);
                                recaptchaRef.current.execute();
                            }}
                        >
                            {({ isSubmitting }) => (
                                <Form style={{ textAlign: 'left' }} noValidate autoComplete='off'>

                                    <Div sx={{ mb: 3, mt: 1 }}>
                                        <JumboTextField
                                            fullWidth
                                            name="email"
                                            label="E-Posta veya Kimlik No"
                                        />
                                    </Div>
                                    <Div sx={{ mb: 2, mt: 1 }}>
                                        <JumboTextField
                                            fullWidth
                                            name="password"
                                            label="Şifre"
                                            type="password"
                                        />
                                    </Div>
                                    <Typography
                                        textAlign={"right"}
                                        variant={"body1"}
                                    >
                                        <Link underline="none" href={config.mainPageUrl}>Şifrenizi mi unuttunuz?</Link>
                                    </Typography>
                                    <Div sx={{ mb: 1 }}>
                                        <FormControlLabel control={<Checkbox />} label="Beni hatırla" />
                                    </Div>
                                    {siteKey && (
                                        <ReCAPTCHA
                                            ref={recaptchaRef}
                                            size="invisible"
                                            sitekey={siteKey}
                                            asyncScriptOnLoad={() => setRecaptchaLoaded(true)}
                                            onChange={handleRecaptchaChange}
                                            onErrored={handleRecaptchaError}
                                            onExpired={handleRecaptchaExpired}
                                        />
                                    )}
                                    <LoadingButton
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        sx={{ mb: 3 }}
                                        loading={isSubmitting || isLoggingIn}
                                        disabled={!!siteKey && !recaptchaLoaded}
                                    >Giriş Yap</LoadingButton>

                                </Form>
                            )}
                        </Formik>
                    </CardContent>
                </Card>

            </Div>
        </Dialog>
    )
}

export default LoginDialog
